import { useEffect, useRef } from 'react';
import axios from 'config/axios';

type ActivityType = 'test' | 'assignment' | 'vocab_flashcards' | 'vocab_quiz';

// Heartbeat cadence per activity type — longer sittings ping less often.
const HEARTBEAT_INTERVAL_MS: Record<ActivityType, number> = {
    test: 5 * 60_000,          // 5 min
    assignment: 3 * 60_000,    // 3 min
    vocab_flashcards: 60_000,  // 1 min
    vocab_quiz: 60_000,        // 1 min
};
const ACTIVE_TICK_MS = 1_000;

type ActivityIds = {
    studentTestId?: string;
    studentQuestionSetId?: string;
    studentVocabSetId?: number;
    studentVocabTestId?: number;
};

type UseStudySessionArgs = {
    activityType: ActivityType;
    activityIds: ActivityIds;
    // Hold session start until upstream data is loaded (e.g., we don't know the
    // ID yet). When false, the hook is inert until it flips to true.
    enabled?: boolean;
};

export default function useStudySession({
    activityType,
    activityIds,
    enabled = true,
}: UseStudySessionArgs) {
    const sessionIdRef = useRef<string | null>(null);
    // Session-scoped secret used to authenticate the /end/ call from
    // navigator.sendBeacon, which can't attach Authorization headers.
    const endTokenRef = useRef<string | null>(null);
    const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const activeTickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Active-focus seconds — only increments while document.visibilityState=='visible'.
    // This is what we send to the server as the authoritative study time on /end/.
    const activeSecondsRef = useRef(0);
    const endedRef = useRef(false);

    // Stable serialization of activityIds so the effect re-runs when the ID changes.
    const activityIdsKey = JSON.stringify(activityIds);

    useEffect(() => {
        if (!enabled) return;

        const hasAnyId = Object.values(activityIds).some(v => v !== undefined && v !== null);
        if (!hasAnyId) return;

        let cancelled = false;
        endedRef.current = false;
        activeSecondsRef.current = 0;

        const sendHeartbeat = () => {
            if (!sessionIdRef.current || endedRef.current) return;
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            axios.post('studytracking/heartbeat/', {
                studySessionId: sessionIdRef.current,
            }).catch(() => {/* swallow — best-effort */});
        };

        const startHeartbeat = () => {
            if (heartbeatTimerRef.current) return;
            heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS[activityType]);
        };

        const stopHeartbeat = () => {
            if (heartbeatTimerRef.current) {
                clearInterval(heartbeatTimerRef.current);
                heartbeatTimerRef.current = null;
            }
        };

        // The active ticker runs once per second whenever visible. We start it
        // on mount (if visible) and let it run for the lifetime of the session;
        // it checks visibility itself on each tick so we don't churn the timer
        // on every tab switch.
        const startActiveTicker = () => {
            if (activeTickerRef.current) return;
            activeTickerRef.current = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
                if (endedRef.current) return;
                activeSecondsRef.current += 1;
            }, ACTIVE_TICK_MS);
        };

        const stopActiveTicker = () => {
            if (activeTickerRef.current) {
                clearInterval(activeTickerRef.current);
                activeTickerRef.current = null;
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                startHeartbeat();
                sendHeartbeat();
            } else {
                stopHeartbeat();
            }
        };

        // End via sendBeacon so it survives tab close. Falls back to axios when
        // sendBeacon is unavailable. Idempotent on the server.
        const endSession = () => {
            if (!sessionIdRef.current || endedRef.current) return;
            endedRef.current = true;
            // Converge all exit paths (unmount, pagehide, beforeunload) on the
            // same teardown so the intervals don't linger as no-ops if the page
            // is frozen into bfcache rather than unmounted.
            stopHeartbeat();
            stopActiveTicker();
            const activeSeconds = activeSecondsRef.current;
            const endPayload = {
                studySessionId: sessionIdRef.current,
                endToken: endTokenRef.current,
                activeSeconds,
            };
            const payload = JSON.stringify(endPayload);

            // SERVER_BASE_URL may or may not have a trailing slash — normalize.
            const baseUrl = (process.env.SERVER_BASE_URL ?? '').replace(/\/+$/, '');
            const beaconUrl = `${baseUrl}/studytracking/end/`;
            const beaconBlob = new Blob([payload], { type: 'application/json' });

            // sendBeacon doesn't support custom headers, so we can't attach the
            // Firebase ID token here. Use it only as a best-effort fallback for
            // unload. The primary close path is the axios call below (run on
            // React unmount, which fires before navigation in SPAs).
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                try { navigator.sendBeacon(beaconUrl, beaconBlob); } catch { /* ignore */ }
            }

            // Best effort authenticated close. Won't run if the tab is already
            // gone, but covers SPA navigation and explicit submit flows.
            axios.post('studytracking/end/', endPayload)
                .catch(() => {/* swallow — end is idempotent */});
        };

        const onPageHide = () => endSession();
        const onBeforeUnload = () => endSession();

        // Defer the /start/ call so React strict-mode double-mounts don't
        // create + immediately end a throwaway session. The first effect's
        // cleanup clears this timer before the request ever fires.
        const startTimer = setTimeout(() => {
            axios.post<{ studySessionId: string; endToken: string }>(
                'studytracking/start/', { activityType, ...activityIds },
            )
                .then(res => {
                    if (cancelled) {
                        // Component unmounted between request send and response —
                        // close immediately to avoid an orphan open session. Pass
                        // endToken too so it still closes if the auth header is
                        // momentarily unavailable during the unmount/logout flow.
                        axios.post('studytracking/end/', {
                            studySessionId: res.data.studySessionId,
                            endToken: res.data.endToken,
                        }).catch(() => {});
                        return;
                    }
                    sessionIdRef.current = res.data.studySessionId;
                    endTokenRef.current = res.data.endToken ?? null;
                    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
                        startHeartbeat();
                    }
                    startActiveTicker();
                    if (typeof document !== 'undefined') {
                        document.addEventListener('visibilitychange', onVisibilityChange);
                    }
                    if (typeof window !== 'undefined') {
                        window.addEventListener('pagehide', onPageHide);
                        window.addEventListener('beforeunload', onBeforeUnload);
                    }
                })
                .catch(err => {
                    // Study tracking is non-critical, so we don't surface this to
                    // the student — but log outside production so an integration
                    // mismatch (e.g. wrong activityIds for the activityType) is
                    // discoverable instead of silently missing from analytics.
                    if (process.env.NODE_ENV !== 'production') {
                        // eslint-disable-next-line no-console
                        console.warn('useStudySession: /start/ failed', err);
                    }
                });
        }, 50);

        return () => {
            cancelled = true;
            clearTimeout(startTimer);
            stopHeartbeat();
            stopActiveTicker();
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('pagehide', onPageHide);
                window.removeEventListener('beforeunload', onBeforeUnload);
            }
            endSession();
            sessionIdRef.current = null;
            endTokenRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityType, activityIdsKey, enabled]);
}
